import React, { useEffect, useMemo } from 'react';
import { Form, Typography, Button, Card, Divider, Alert, message } from 'antd';
import { SaveOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedSMPLSystem = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const DESTINATION_SMPLSYSTEM = SOURCES.DESTINATION_SMPLSYSTEM || "smplsystem";

    const { mutate: saveSMPL, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("SMPL System configuration saved successfully")
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_SMPLSYSTEM);
            if (config) {
                form.setFieldsValue({
                    webformLink: config.secretKey,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        saveSMPL({
            secretKey: values.webformLink,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_SMPLSYSTEM,
        });
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    className="rounded-xl bg-blue-50 border-blue-100 mb-6"
                    message={
                        <Text className="text-sm leading-relaxed">
                            <Text strong>Leads Form Link:</Text> Enter the URL for your SMPL System leads form.
                        </Text>
                    }
                />
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Leads Form Link",
            name: "webformLink",
            placeholder: "Enter URL",
            required: true,
            type: "url",
            span: 24,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "Leads limit",
            type: "number",
            span: 24,
        }
    ], []);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Connect your SMPL System account and sync leads.</Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />
                <Divider className="mb-4 border-gray-100" />
                <div className="flex justify-start">
                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedSMPLSystem;
